import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  StickerHeart, StickerStar, StickerFlower, StickerBolt, StickerPeace,
  StickerSunflower, StickerWave, StickerShoe,
} from "../components/Stickers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Placeholder supporters. Edit names / colors / notes here (or via Visual Edit Mode).
   Each entry: { name, color, note? } */
const COLORS = ["#F87171", "#F4A261", "#5BA8C7", "#B57FA0", "#2A9D8F", "#B85450"];
const SUPPORTERS = [
  { name: "Carly & Easton", color: COLORS[0], note: "ride or dies" },
  { name: "Mom & Papa", color: COLORS[1] },
  { name: "Mary-Ann Wayne", color: COLORS[2], note: "the '85 Westy" },
  { name: "Rob", color: COLORS[3], note: "the wheels — drove the Westy" },
  { name: "Winnipeg Crew", color: COLORS[4] },
  { name: "Every Livestream Viewer", color: COLORS[5], note: "you know who you are" },
  { name: "Anonymous Donors", color: COLORS[0] },
  { name: "Press Coverage", color: COLORS[1], note: "local & national" },
  { name: "Terry Fox Foundation Family", color: COLORS[2] },
];

const Sponsors = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", interest: "sponsor", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sponsors`, form);
      setSent(true);
      setForm({ name: "", email: "", organization: "", interest: "sponsor", message: "" });
      toast.success("Thanks! I'll reach out personally.");
    } catch {
      toast.error("Couldn't send. Please email me directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Toaster position="top-center" richColors />
      <section className="bg-[#2A9D8F] border-b-4 border-black relative overflow-hidden">
        <div className="absolute top-8 left-8 hidden md:block"><StickerStar size={70} rotate={-10} className="float-slow" /></div>
        <div className="absolute bottom-8 right-8 hidden md:block"><StickerHeart size={80} rotate={14} className="float-med" /></div>
        <div className="max-w-7xl mx-auto py-20 px-6">
          <div className="font-pixel uppercase tracking-widest text-lg text-black/80">▒▒ partners & donors ▒▒</div>
          <h1 className="font-display tracking-wider text-6xl sm:text-7xl lg:text-8xl mt-3 text-black">SPONSORS</h1>
          <p className="font-body text-xl mt-5 max-w-3xl text-black/90">
            I'm building toward founding a nonprofit dedicated to supporting cancer patients and the research that will end this disease.
            That work will take a team, and a community of supporters behind it.
          </p>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 px-6 relative">
        <div className="absolute top-10 right-10 hidden md:block"><StickerFlower size={80} rotate={-10} className="float-slow" /></div>
        <div className="absolute bottom-10 left-10 hidden md:block"><StickerPeace size={80} rotate={12} className="float-med" /></div>
        <div className="absolute top-1/2 left-2 hidden xl:block"><StickerWave size={90} rotate={8} className="float-slow" /></div>
        <div className="absolute bottom-1/3 right-2 hidden xl:block"><StickerShoe size={70} rotate={-10} className="float-med" /></div>

        <div className="max-w-5xl mx-auto">
          <h2 className="font-display tracking-wider text-5xl text-black">THE VISION</h2>
          <div className="hazard h-3 mt-3 mb-8" />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: "Support patients", b: "Direct support for families navigating treatment, the practical things insurance doesn't cover.", c: "#F87171" },
              { t: "Fund the search", b: "Channel donations into research moving us closer to a cure.", c: "#5BA8C7" },
              { t: "Move people", b: "Storytelling, runs, and events that get communities involved.", c: "#F4A261" },
            ].map((v) => (
              <div key={v.t} className="bg-white border-4 border-black shadow-[8px_8px_0_#000] p-6">
                <div className="font-display tracking-wider text-3xl" style={{ color: v.c }}>{v.t}</div>
                <p className="font-body text-base mt-3">{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="bg-[#0A0A0A] text-white py-20 px-6 border-y-4 border-black relative overflow-hidden">
        <div className="absolute top-10 left-10 hidden md:block"><StickerBolt size={80} rotate={-12} className="float-slow" /></div>
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display tracking-wider text-5xl text-[#F4D58D]">WAYS TO SHOW UP</h2>
          <p className="font-body text-lg mt-3 max-w-3xl text-white/80">
            Whether you're a brand, a community, or one person who cares, there's room.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {[
              { t: "Title Partner", b: "Lead sponsor on a major run or live-streamed event. Co-branded storytelling.", color: "#F87171" },
              { t: "Brand Sponsor", b: "Gear, fuel, gas, vehicle support, signage. Get featured on socials & stream.", color: "#8ECAE6" },
              { t: "Individual Donor", b: "Personal contributions of any size, recognized on the wall of supporters.", color: "#F4D58D" },
            ].map((tier) => (
              <div key={tier.t} className="bg-white text-black border-4 border-black p-6 shadow-[8px_8px_0_#F87171]" data-testid={`tier-${tier.t}`}>
                <div className="font-display tracking-wider text-3xl" style={{ color: tier.color }}>{tier.t}</div>
                <p className="font-body text-base mt-3">{tier.b}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-[#F4D58D] text-black border-4 border-black p-5 font-pixel text-xl shadow-[6px_6px_0_#F87171] max-w-3xl">
            <span className="blink mr-2 text-[#F87171]">●</span>
            The nonprofit is in early planning. For now, sponsorships support upcoming CrossCanadaJax events and content. We'll be transparent every step of the way.
          </div>
        </div>
      </section>

      {/* WALL OF SUPPORTERS */}
      <section className="bg-[#FFF4D6] border-b-4 border-black py-20 px-6 relative overflow-hidden" data-testid="wall-of-supporters">
        <div className="absolute top-10 left-10 hidden md:block"><StickerSunflower size={90} rotate={-12} className="float-slow" /></div>
        <div className="absolute bottom-10 right-10 hidden md:block"><StickerShoe size={90} rotate={10} className="float-med" /></div>
        <div className="absolute top-1/2 right-4 hidden xl:block"><StickerWave size={90} rotate={8} className="float-slow" /></div>

        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-[#2A9D8F] text-black border-4 border-black px-4 py-1 font-display tracking-wider text-2xl shadow-[4px_4px_0_#000] mb-3">
            ★ THE WALL ★
          </div>
          <h2 className="font-display tracking-wider text-5xl sm:text-6xl text-black leading-none"
              style={{ textShadow: "4px 4px 0 rgba(0,0,0,0.18)" }}>
            WALL OF SUPPORTERS
          </h2>
          <p className="font-body text-lg mt-5 max-w-3xl text-black/80">
            Every name here is someone who showed up. Donors, brand partners, and the people who shared,
            cheered, fed, and hosted me on the road. None of the big numbers happen without them.
          </p>

          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-10" data-testid="supporters-grid">
            {SUPPORTERS.map((s, i) => {
              const rot = (i % 5) * 1.4 - 2.8;
              return (
                <li
                  key={i}
                  className="bg-white border-4 border-black p-4 shadow-[5px_5px_0_#000] text-center"
                  style={{ transform: `rotate(${rot}deg)` }}
                  data-testid={`supporter-${i}`}
                >
                  <div className="font-display tracking-wider text-xl leading-tight" style={{ color: s.color }}>
                    {s.name}
                  </div>
                  {s.note && (
                    <div className="font-pixel text-sm text-black/60 mt-1">{s.note}</div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-10 bg-black text-white border-4 border-black p-6 shadow-[8px_8px_0_#F4D58D] flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <div className="font-display tracking-wider text-2xl text-[#F4D58D]">Want your name on the wall?</div>
              <p className="font-pixel text-lg text-white/80 mt-1">
                Donate, sponsor a project, or just send a note. Every supporter gets added.
              </p>
            </div>
            <a href="#sponsor-form"
              className="font-display tracking-wider text-xl px-6 py-3 bg-[#F87171] text-white border-4 border-white shadow-[4px_4px_0_#fff] btn-press"
              data-testid="wall-cta">
              Join the wall →
            </a>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="sponsor-form" className="py-20 px-6 relative">
        <div className="absolute top-6 right-6 hidden md:block"><StickerHeart size={70} rotate={14} className="float-slow" /></div>

        <div className="max-w-3xl mx-auto">
          {sent ? (
            <div className="bg-white border-4 border-black shadow-[8px_8px_0_#000] p-8 text-center" data-testid="sponsor-success">
              <div className="font-display tracking-wider text-5xl text-[#2A9D8F]">THANKS!</div>
              <p className="font-body text-lg mt-4">Got it. I'll reach out personally. These messages don't go to a black hole.</p>
              <button onClick={() => setSent(false)} data-testid="sponsor-send-another"
                className="mt-6 font-display text-2xl tracking-wider px-6 py-2 bg-[#F4D58D] border-4 border-black shadow-[4px_4px_0_#000] btn-press">
                Send another →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border-4 border-black shadow-[10px_10px_0_#000] p-6 sm:p-8 space-y-4" data-testid="sponsor-form">
              <h3 className="font-display tracking-wider text-4xl text-black">GET INVOLVED</h3>
              <p className="font-pixel text-xl text-black/70">Tell me how you'd like to support and I'll be in touch.</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name *">
                  <input required type="text" value={form.name} onChange={update("name")}
                    className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                    data-testid="sponsor-name" />
                </Field>
                <Field label="Email *">
                  <input required type="email" value={form.email} onChange={update("email")}
                    className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                    data-testid="sponsor-email" />
                </Field>
                <Field label="Organization (if any)">
                  <input type="text" value={form.organization} onChange={update("organization")}
                    className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                    data-testid="sponsor-org" />
                </Field>
                <Field label="I'm interested as a...">
                  <select value={form.interest} onChange={update("interest")}
                    className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none"
                    data-testid="sponsor-interest">
                    <option value="sponsor">Brand sponsor</option>
                    <option value="donor">Individual donor</option>
                    <option value="partner">Title / partner</option>
                    <option value="other">Other / not sure</option>
                  </select>
                </Field>
              </div>

              <Field label="Your message *">
                <textarea required rows={5} value={form.message} onChange={update("message")}
                  className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none resize-none focus:bg-[#FFFEC2]"
                  placeholder="What are you thinking? What event or project caught your eye?"
                  data-testid="sponsor-message" />
              </Field>

              <button type="submit" disabled={submitting} data-testid="sponsor-submit"
                className="w-full font-display tracking-wider text-3xl uppercase px-6 py-4 bg-[#2A9D8F] text-black border-4 border-black shadow-[8px_8px_0_#000] btn-press disabled:opacity-60">
                {submitting ? "Sending..." : "Send Message →"}
              </button>
              <p className="font-pixel text-base text-black/60 text-center">
                Or email <a href="mailto:CrossCanadaJax97@gmail.com" className="retro-link">CrossCanadaJax97@gmail.com</a>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-pixel text-lg uppercase tracking-wider text-black/70 block mb-1">{label}</span>
    {children}
  </label>
);

export default Sponsors;
